import {
	ROAD_AI_OBJECTIVES,
	ROAD_AI_RUN_ERROR_CODES,
	type RoadAiCandidateEntity,
	type RoadAiCandidateState,
	type RoadAiDataSelection,
	type RoadAiDiagnostic,
	type RoadAiJobProgress,
	type RoadAiMetrics,
	type RoadAiObjective,
	type RoadAiRunErrorCode,
	type RoadAiViolation,
} from "./roadAi.types";
import type { RoadAiRouteProposalBody } from "./roadAiApiClient";

/** 法面勾配比の既定値（会話では扱わないため client 側の固定値）。 */
export const ROAD_AI_DEFAULT_SLOPE_RATIO = 0.1;

/** 会話から確定した実行パラメータ。 */
export interface RoadAiRunParams {
	start: readonly number[];
	end: readonly number[];
	waypoints: readonly (readonly number[])[];
	roadWidthM: number;
	maxSlopePercent: number;
	objectives: readonly RoadAiObjective[];
	slopeRatio?: number;
	waypointToleranceM?: number;
	cfDiffEvaluator?: "proxy" | "global";
	topKPerObjective?: number;
	searchBackend?: "a_star" | "hybrid_a_star";
	objectiveWeight?: number;
}

/** 使用データと会話パラメータから受付リクエストを組み立てる。 */
export const convertSelectionToRequest = (
	selection: RoadAiDataSelection,
	params: RoadAiRunParams,
): RoadAiRouteProposalBody => ({
	source:
		selection.mode === "standard"
			? {
					mode: "standard",
					pointCloudId: selection.pointCloud.pointCloudId,
					designAssetId: selection.design.assetId,
				}
			: {
					mode: "point_cloud_only",
					pointCloudId: selection.pointCloud.pointCloudId,
				},
	route: {
		start: toPoint(params.start),
		end: toPoint(params.end),
		waypoints: params.waypoints.map(toPoint),
		waypointToleranceM: params.waypointToleranceM ?? 5.0,
	},
	constraints: {
		roadWidthM: params.roadWidthM,
		maxSlopePercent: params.maxSlopePercent,
		slopeRatio: params.slopeRatio ?? ROAD_AI_DEFAULT_SLOPE_RATIO,
	},
	objectives: [...params.objectives],
	options: {
		cfDiffEvaluator: params.cfDiffEvaluator ?? "proxy",
		topKPerObjective: params.topKPerObjective ?? 1,
		searchBackend: params.searchBackend ?? "hybrid_a_star",
		objectiveWeight: params.objectiveWeight ?? 1.0,
	},
});

/**
 * ジョブ照会レスポンス（camelCase）を client 側の進捗表現へ畳む。
 * 候補経路は id 付きの正規化状態にする。
 */
export const convertJobResultToProgress = (
	body: unknown,
): RoadAiJobProgress => {
	if (!isRecord(body)) return { status: "pending" };
	if (body.status !== "succeeded") return { status: "pending" };

	const rawCandidates = Array.isArray(body.candidates) ? body.candidates : [];
	const rawDiagnostics = Array.isArray(body.diagnostics)
		? body.diagnostics
		: [];

	const candidateState: RoadAiCandidateState = { ids: [], entities: {} };
	const seenPerObjective: Record<string, number> = {};
	for (const raw of rawCandidates) {
		if (!isRecord(raw)) continue;
		const objective = toObjective(raw.objective);
		const index = seenPerObjective[objective] ?? 0;
		seenPerObjective[objective] = index + 1;
		const id = `${objective}#${index}`;
		const entity: RoadAiCandidateEntity = {
			id,
			objective,
			points: toNumberArray(raw.points),
			metricsFinal: toMetrics(raw.metricsFinal),
			nodeCount: toNumber(raw.nodeCount) ?? 0,
			violations: toViolations(raw.violations),
			isFallback: raw.isFallback === true,
		};
		candidateState.ids.push(id);
		candidateState.entities[id] = entity;
	}

	return {
		status: "succeeded",
		candidateState,
		diagnostics: rawDiagnostics.filter(isRecord).map(toDiagnostic),
	};
};

/**
 * 正規化状態を既存表示コンポーネント互換の snake_case 候補配列へ戻す。
 * `runPrototypeRoadAI` の戻り値の形を変えないための唯一の変換点。
 */
export const convertCandidateStateToLegacy = (
	state: RoadAiCandidateState,
): {
	objective: string;
	points: number[];
	metrics_final: RoadAiMetrics;
	node_count: number;
	violations: RoadAiViolation[];
	is_fallback: boolean;
}[] =>
	state.ids
		.map((id) => state.entities[id])
		.filter((entity): entity is RoadAiCandidateEntity => Boolean(entity))
		.map((entity) => ({
			objective: entity.objective,
			points: [...entity.points],
			metrics_final: entity.metricsFinal,
			node_count: entity.nodeCount,
			violations: entity.violations.map((violation) => ({ ...violation })),
			is_fallback: entity.isFallback,
		}));

/**
 * 受付・照会の HTTP エラー応答を実行中止の理由コードへ写像する。
 * v2-server は本文の `error` にコードを載せる（同一ステータスに複数の原因があるため）。
 */
export const convertHttpErrorToRunErrorCode = (
	status: number,
	body: unknown,
): RoadAiRunErrorCode => {
	const code =
		isRecord(body) && typeof body.error === "string" ? body.error : "";
	if ((ROAD_AI_RUN_ERROR_CODES as readonly string[]).includes(code)) {
		return code as RoadAiRunErrorCode;
	}
	// 本文にコードが無い 400 は入力不正の総称へ落とす。原因は座標や型の不正・
	// 中継点の上限超過・siteId の書式不正など複数あり、特定の原因を騙らない。
	return status === 400 ? "INVALID_REQUEST" : "ROUTE_ENGINE_FAILED";
};

const toPoint = (value: readonly number[]): [number, number, number] => [
	Number(value[0]),
	Number(value[1]),
	Number(value[2]),
];

const toObjective = (value: unknown): RoadAiObjective =>
	typeof value === "string" &&
	(ROAD_AI_OBJECTIVES as readonly string[]).includes(value)
		? (value as RoadAiObjective)
		: "cf_diff";

const toMetrics = (value: unknown): RoadAiMetrics => {
	const record = isRecord(value) ? value : {};
	return {
		cut: toNumber(record.cut) ?? 0,
		fill: toNumber(record.fill) ?? 0,
		over_cut: toNumber(record.over_cut),
		over_fill: toNumber(record.over_fill),
		cf_diff: toNumber(record.cf_diff) ?? 0,
	};
};

const toViolations = (value: unknown): RoadAiViolation[] =>
	(Array.isArray(value) ? value : []).filter(isRecord).map((violation) => ({
		type: violation.type === "slope_ratio" ? "slope_ratio" : "road_width_m",
		message: typeof violation.message === "string" ? violation.message : "",
		detail: isRecord(violation.detail) ? violation.detail : {},
	}));

const toDiagnostic = (value: Record<string, unknown>): RoadAiDiagnostic => ({
	code: typeof value.code === "string" ? value.code : "",
	message: typeof value.message === "string" ? value.message : "",
	detail: isRecord(value.detail) ? value.detail : {},
});

const toNumberArray = (value: unknown): number[] =>
	(Array.isArray(value) ? value : []).filter(
		(item): item is number => typeof item === "number",
	);

const toNumber = (value: unknown): number | null =>
	typeof value === "number" && Number.isFinite(value) ? value : null;

const isRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === "object" && value !== null;
